'use client';
import React from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { Globe, Target, Lightbulb, Users, Award, TrendingUp } from 'lucide-react';

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  initials: string;
  color: string;
}

const TEAM: TeamMember[] = [
  { name: 'Dr. Arjun Mehta', role: 'Co-Founder & CEO', bio: 'Former IIT Delhi professor with 15 years in STEM education. Led national olympiad programs for 200,000+ students.', initials: 'AM', color: 'bg-primary' },
  { name: 'Priya Nair', role: 'Co-Founder & Chief Academic Officer', bio: 'PhD in Cognitive Science from MIT. Designed AI-powered assessment frameworks used in 12 countries.', initials: 'PN', color: 'bg-secondary' },
  { name: 'Liang Wei', role: 'CTO', bio: 'Ex-Google engineer. Built scalable exam infrastructure handling 500,000+ concurrent users across Asia.', initials: 'LW', color: 'bg-accent' },
  { name: 'Sarah Okonkwo', role: 'Head of Partnerships', bio: 'Former UNESCO education advisor. Established school partnerships across Africa, Europe, and South Asia.', initials: 'SO', color: 'bg-green-600' },
  { name: 'Rahul Sharma', role: 'Head of AI & Analytics', bio: 'ML researcher specialising in adaptive learning systems. Published 20+ papers on educational AI.', initials: 'RS', color: 'bg-purple-600' },
  { name: 'Emma Brandt', role: 'Head of Student Experience', bio: 'Former Olympiad gold medalist. Designs the student journey from first exam to global recognition.', initials: 'EB', color: 'bg-rose-500' },
];

const STATS = [
  { value: '120,000+', label: 'Students Registered', icon: <Users size={18} className="text-primary" /> },
  { value: '40+', label: 'Countries Represented', icon: <Globe size={18} className="text-secondary" /> },
  { value: '7', label: 'Olympiad Subjects', icon: <Award size={18} className="text-accent" /> },
  { value: '98.4%', label: 'Student Satisfaction', icon: <TrendingUp size={18} className="text-green-600" /> },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Topbar />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="bg-hero-gradient py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-semibold uppercase tracking-widest mb-6">
              <Globe size={12} />
              About OnlineOlympiads.org
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
              Building the World&apos;s Most Trusted<br />
              <span className="text-gradient-primary" style={{ WebkitTextFillColor: '#0EA5E9' }}>Academic Benchmark Platform</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
              We believe every student — regardless of geography or background — deserves access to world-class academic challenges and the recognition they earn.
            </p>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-card border-b border-border py-8 px-6">
          <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="flex justify-center mb-2">{s.icon}</div>
                <p className="text-2xl font-extrabold text-foreground stat-counter">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                <Target size={20} className="text-primary" />
              </div>
              <h2 className="text-xl font-extrabold text-foreground mb-3">Our Mission</h2>
              <p className="text-muted-foreground leading-relaxed text-sm">
                To democratise access to rigorous, internationally recognised STEM olympiads — giving every student from Grade 1 to Grade 12 the tools to benchmark their abilities, discover their strengths, and compete on a global stage.
              </p>
              <p className="text-muted-foreground leading-relaxed text-sm mt-3">
                We combine the rigour of traditional olympiad assessments with AI-powered insights, so students don&apos;t just receive a score — they receive a roadmap for growth.
              </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center mb-5">
                <Lightbulb size={20} className="text-secondary" />
              </div>
              <h2 className="text-xl font-extrabold text-foreground mb-3">Our Vision</h2>
              <p className="text-muted-foreground leading-relaxed text-sm">
                A world where academic talent is identified and celebrated regardless of where a student lives. We envision OnlineOlympiads.org as the global standard for student achievement — a credential recognised by universities, employers, and institutions worldwide.
              </p>
              <p className="text-muted-foreground leading-relaxed text-sm mt-3">
                By 2030, we aim to serve 1 million students across 100 countries, making STEM excellence universally accessible.
              </p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-12 px-6 bg-muted/40 border-y border-border">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-extrabold text-foreground mb-6 text-center">How We Started</h2>
            <div className="space-y-4 text-muted-foreground text-sm leading-relaxed">
              <p>
                OnlineOlympiads.org was founded in 2024 by a group of educators, technologists, and former olympiad participants who saw a gap: millions of talented students around the world had no accessible, credible way to test themselves against global peers.
              </p>
              <p>
                Traditional olympiads were expensive, geographically limited, and offered little feedback beyond a rank. We set out to build something different — a platform that combines the prestige of international competition with the personalisation of AI-powered learning analytics.
              </p>
              <p>
                Today, OnlineOlympiads.org hosts olympiads in Mathematics, Science, Chemistry, Physics, Biology, Computing & AI, and Astronomy — with students from 40+ countries competing every month. Our AI-generated report cards give every student a detailed breakdown of their performance, not just a number.
              </p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-extrabold text-foreground mb-3">Meet the Team</h2>
              <p className="text-muted-foreground text-sm max-w-xl mx-auto">
                Educators, engineers, and researchers united by a single goal: making academic excellence accessible to every student on the planet.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {TEAM.map((member) => (
                <div key={member.name} className="bg-card border border-border rounded-2xl p-6 bg-card-hover">
                  <div className={`w-12 h-12 rounded-xl ${member.color} flex items-center justify-center text-white font-extrabold text-sm mb-4`}>
                    {member.initials}
                  </div>
                  <h3 className="font-bold text-foreground text-sm">{member.name}</h3>
                  <p className="text-xs text-secondary font-semibold mt-0.5 mb-3">{member.role}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-12 px-6 bg-muted/40 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-extrabold text-foreground mb-8 text-center">What We Stand For</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { title: 'Rigour Without Barriers', desc: 'World-class assessments that any student with an internet connection can access.' },
                { title: 'Transparency in Assessment', desc: 'Every score comes with a detailed breakdown — no black boxes, no mystery grades.' },
                { title: 'Privacy by Design', desc: 'Student data is protected with enterprise-grade security. We never sell personal data.' },
                { title: 'Global, Not Parochial', desc: 'Our leaderboards, certificates, and benchmarks are designed for a borderless world.' },
              ].map((v) => (
                <div key={v.title} className="bg-card border border-border rounded-xl p-5">
                  <h3 className="font-bold text-foreground text-sm mb-1">{v.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
